/**
 * بانر هديّة عيد الميلاد — يَظهر للعميل الذي لديه عرض نشط فقط.
 * - يُحضِر تكوين البانر العامّ + عرض العميل الحاليّ.
 * - يحترم الإعدادات: enabled · pages · position · variant · ألوان · نصّ · dismissible · showFor.
 * - الإغلاق يُحفظ في sessionStorage فقط (يعود في الزيارة التالية).
 * - مركَّب مرّة واحدة في SiteShell، يكتشف الصفحة آلياً من useLocation.
 */
import * as React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import {
  getBirthdayBannerConfig,
  getMyPendingBirthdayGift,
} from "@/utils/birthdayGifts.functions";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { matchPagePattern } from "@/lib/page-pattern";

const HIDDEN_PREFIXES = ["/admin", "/driver", "/auth", "/a/", "/l/"];

function pageMatches(pathname: string, search: string, pages: string[]): boolean {
  if (pages.includes("all")) return true;
  return pages.some((p) => matchPagePattern(pathname, search, p));
}

export function BirthdayGiftBanner() {
  const { pathname } = useLocation();
  const search = typeof window !== "undefined" ? window.location.search : "";
  const { user, loading: authLoading } = useAuth();
  const { locale } = useLocale();
  const isAr = locale === "ar";

  const fetchBanner = useServerFn(getBirthdayBannerConfig);
  const fetchOffer = useServerFn(getMyPendingBirthdayGift);

  const bannerQ = useQuery({
    queryKey: ["birthday-banner-config"],
    queryFn: () => fetchBanner(),
    staleTime: 5 * 60_000,
    enabled: !!user && !authLoading,
  });
  const offerQ = useQuery({
    queryKey: ["my-birthday-gift", user?.id ?? null],
    queryFn: () => fetchOffer(),
    enabled: !!user && !authLoading,
    staleTime: 60_000,
  });

  const [dismissed, setDismissed] = React.useState(false);
  const offerId = offerQ.data?.id ?? null;
  React.useEffect(() => {
    if (!offerId) return;
    const key = `birthday-banner-dismissed:${offerId}`;
    if (typeof window !== "undefined" && sessionStorage.getItem(key) === "1") {
      setDismissed(true);
    } else {
      setDismissed(false);
    }
  }, [offerId]);

  // فلترة سريعة
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  if (!user || authLoading) return null;
  if (!bannerQ.data?.enabled) return null;
  if (!offerQ.data) return null;
  if (dismissed) return null;

  const cfg = bannerQ.data;
  const offer = offerQ.data;

  if (!pageMatches(pathname, search, cfg.pages ?? ["all"])) return null;

  const status = offer.status as "pending" | "claimed" | "expired";
  if (status !== "expired") {
    if (cfg.showFor === "pending" && status !== "pending") return null;
    if (cfg.showFor === "claimed" && status !== "claimed") return null;
  }
  // status==='expired' لا يخضع لـshowFor — ظهوره محكوم بإعداد showAfterExpiry
  // الذي تحقّقت منه دالّة getMyPendingBirthdayGift على السيرفر.

  const defaultPendingTextAr = "🎁 لديك هديّة عيد ميلاد بانتظارك!";
  const defaultPendingTextEn = "🎁 Your birthday gift is waiting!";
  const defaultClaimedTextAr = "✨ هديّتك مسجَّلة — نُجهّزها لك";
  const defaultClaimedTextEn = "✨ Your gift is on its way";
  const defaultExpiredTextAr = "🎁 فاتتك هديّة عيد ميلادك — تابعنا لفرص قادمة";
  const defaultExpiredTextEn = "🎁 You missed your birthday gift — stay tuned for more";

  const pickText = () => {
    if (status === "expired") {
      return isAr
        ? (cfg.textArExpired || defaultExpiredTextAr)
        : (cfg.textEnExpired || defaultExpiredTextEn);
    }
    return isAr
      ? (cfg.textAr || (status === "pending" ? defaultPendingTextAr : defaultClaimedTextAr))
      : (cfg.textEn || (status === "pending" ? defaultPendingTextEn : defaultClaimedTextEn));
  };
  const text = pickText();

  const cta = isAr
    ? (cfg.ctaAr || (status === "pending" ? "اختر هديّتك" : "عرض الهديّة"))
    : (cfg.ctaEn || (status === "pending" ? "Claim now" : "View gift"));
  const showCta = status !== "expired";

  const style: React.CSSProperties = {
    background: cfg.bgColor || undefined,
    color: cfg.textColor || undefined,
    // إزاحة فوق الناف بار السفليّ للموبايل عند الظهور أسفل الشاشة
    ...(cfg.position === "bottom" ? { marginBottom: "var(--mobile-nav-height, 0rem)" } : {}),
  };

  const onClose = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(`birthday-banner-dismissed:${offer.id}`, "1");
    } catch {}
  };

  const href = status === "expired" ? "/account" : `/account/gifts/birthday/${offer.id}`;

  // ── أشكال + مواضع ──
  if (cfg.variant === "strip") {
    const positionClass = cfg.position === "bottom"
      ? "fixed bottom-0 inset-x-0"
      : "fixed top-0 inset-x-0";
    return (
      <div
        className={`${positionClass} z-[60] border-b border-amber-300/40 bg-gradient-to-r from-amber-100 via-amber-50 to-amber-100 text-amber-900 `}
        style={style}
        role="region"
        aria-label={isAr ? "بانر هديّة عيد الميلاد" : "Birthday gift banner"}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2 text-sm">
          <Link to={href} className="flex-1 truncate font-medium hover:underline">
            {text}
          </Link>
          {showCta && (
            <Link
              to={href}
              className="shrink-0 rounded-full bg-amber-900 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-950"
            >
              {cta}
            </Link>
          )}
          {cfg.dismissible && (
            <button
              onClick={onClose}
              className="shrink-0 rounded-full p-1 hover:bg-amber-200/60"
              aria-label={isAr ? "إغلاق" : "Close"}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (cfg.variant === "pill" || cfg.position.startsWith("floating")) {
    const side = cfg.position === "floating-bottom-left" ? "start-4" : "end-4";
    return (
      <div
        className={`fixed bottom-20 ${side} z-[60] max-w-[280px] rounded-full bg-amber-50 px-4 py-2  ring-1 ring-amber-200`}
        style={style}
      >
        <div className="flex items-center gap-2 text-sm text-amber-900">
          <Link to={href} className="flex-1 truncate font-medium hover:underline">
            {text}
          </Link>
          {cfg.dismissible && (
            <button
              onClick={onClose}
              className="shrink-0 rounded-full p-1 hover:bg-amber-200/60"
              aria-label={isAr ? "إغلاق" : "Close"}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {showCta && (
          <Link
            to={href}
            className="mt-1 block rounded-full bg-amber-900 px-3 py-1 text-center text-xs font-semibold text-amber-50 hover:bg-amber-950"
          >
            {cta}
          </Link>
        )}
      </div>
    );
  }

  // variant === 'card'
  const positionClass = cfg.position === "bottom"
    ? "fixed bottom-4 inset-x-4 sm:inset-x-auto sm:end-4 sm:max-w-sm"
    : "fixed top-24 inset-x-4 sm:inset-x-auto sm:end-4 sm:max-w-sm";
  return (
    <div
      className={`${positionClass} z-[60] rounded-none border border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100 p-4 `}
      style={style}
      role="region"
      aria-label={isAr ? "بطاقة هديّة عيد الميلاد" : "Birthday gift card"}
    >
      {cfg.dismissible && (
        <button
          onClick={onClose}
          className="absolute end-2 top-2 rounded-full p-1 hover:bg-amber-200/60"
          aria-label={isAr ? "إغلاق" : "Close"}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <div className="text-3xl">🎂</div>
      <p className="mt-2 font-semibold text-amber-900">{text}</p>
      {showCta && (
        <Link
          to={href}
          className="mt-3 inline-block rounded-full bg-amber-900 px-4 py-2 text-sm font-semibold text-amber-50 hover:bg-amber-950"
        >
          {cta}
        </Link>
      )}
    </div>
  );
}
